import {
  Props,
  ChakraStylesConfig,
  Select as ChakraSelect,
  createFilter,
  GroupBase,
  OptionBase,
} from 'chakra-react-select';
import { FormErrorMessageProps, ChakraProps, CSSObject } from '@chakra-ui/react';
import { ComponentStyleConfig } from '@chakra-ui/theme';
import { specialFont } from 'constants/theme';
import { FormErrorMessage } from 'components/UI/forms/FormErrorMessage';

export type SelectOption = OptionBase & {
  label: string;
  value: string;
};

export type SelectFilterOption = {
  readonly label: string;
  readonly value: string;
  readonly data: SelectOption | string;
};

export type SelectConfig = {
  readonly ignoreCase?: boolean;
  readonly ignoreAccents?: boolean;
  readonly stringify?: (option: SelectFilterOption) => string;
  readonly trim?: boolean;
  readonly matchFrom?: 'any' | 'start';
};

const SelectStyles: ComponentStyleConfig = {};

interface ISelectChakraStyles {
  control?: CSSObject;
}

export type SelectProps = Props<SelectOption | string, boolean, GroupBase<SelectOption>> & {
  filterConfig?: SelectConfig;
  error?: string;
  errorProps?: FormErrorMessageProps;
  overrideStyles?: ISelectChakraStyles;
} & ChakraProps;

const Select = ({ filterConfig = {}, errorProps = {}, error, name, overrideStyles, ...props }: SelectProps) => {
  const chakraStyles: ChakraStylesConfig<SelectOption | string, boolean, GroupBase<SelectOption>> = {
    dropdownIndicator: (provided) => ({
      ...provided,
      bgColor: 'transparent',
      p: 2,
      border: 'none',
      fontSize: '2xl',
    }),
    clearIndicator: (provided) => ({
      ...provided,
      _hover: {
        bgColor: 'transparent',
      },
      _focus: {
        boxShadow: 'none',
      },
      fontSize: 'sm',
    }),
    indicatorsContainer: (provided) => ({
      ...provided,
      color: 'gray.400',
    }),
    indicatorSeparator: (provided) => ({
      ...provided,
      display: 'none',
    }),
    multiValue: (provided) => ({
      ...provided,
      bgColor: 'primaryThemeColour.400',
      color: 'primaryFontColour.400',
      borderRadius: 0,
      fontFamily: specialFont,
      fontSize: 'sm',
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: 'primaryFontColour.400',
      opacity: 0.8,
      _hover: {
        opacity: 1,
      },
    }),
    option: (provided, state) => ({
      ...provided,
      color: state.isSelected ? 'primaryFontColour.400' : 'base2FontColour.500',
      bgColor: state.isSelected ? 'primaryThemeColour.400' : 'transparent',
      _hover: {
        background: 'primaryThemeColour.400',
        color: 'primaryFontColour.400',
      },
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 2,
    }),
    menuList: (provided) => ({
      ...provided,
      zIndex: 2,
    }),
    control: (provided) => ({
      ...provided,
      bgColor: 'gray.100',
      px: 0,
      border: 'none',
      color: 'base2FontColour.500',
      _focus: {
        boxShadow: 'none',
      },
      ...(overrideStyles?.control ?? {}),
    }),
  };

  return (
    <>
      <ChakraSelect<SelectOption | string, boolean, GroupBase<SelectOption>>
        selectedOptionColor="var(--chakra-colors-primaryThemeColour-400)"
        filterOption={createFilter(filterConfig)}
        chakraStyles={chakraStyles}
        name={name}
        sx={{
          inputContainer: 'white',
          ...props.sx,
        }}
        {...props}
      />
      <FormErrorMessage {...errorProps}>{error}</FormErrorMessage>
    </>
  );
};

export { Select, SelectStyles };
